import { Locale } from "@/lib/i18n";
import { siteContentCompletions } from "@/lib/site-content-completions";

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends Array<infer U>
    ? Array<U>
    : T[K] extends object
      ? DeepPartial<T[K]>
      : T[K];
};

type SiteContent = {
  landing: {
    heroTitle: string;
    heroDescription: string;
    heroCta: string;
    toolsTitle: string;
    toolsDescription: string;
    toolsCta: string;
    useCasesTitle: string;
    useCasesDescription: string;
    useCases: Array<{ title: string; description: string }>;
    useCasesCta: string;
    processTitle: string;
    processDescription: string;
    processSteps: Array<{ number: string; title: string; description: string }>;
    featuresTitle: string;
    featuresDescription: string;
    features: Array<{ title: string; description: string }>;
    testimonialsTitle: string;
    testimonialsDescription: string;
    testimonials: Array<{ text: string; author: string; role: string }>;
    landingContactTitle: string;
    landingContactDescription: string;
    sayHi: string;
    getQuote: string;
    name: string;
    email: string;
    message: string;
    send: string;
    faqTitle: string;
    faqDescription: string;
    faqs: Array<{ question: string; answer: string }>;
  };
  about: {
    title: string;
    intro: string;
    missionTitle: string;
    missionBody: string;
    offerTitle: string;
    offers: string[];
    privacyTitle: string;
    privacyBody: string;
    freeTitle: string;
    freeBody: string;
  };
  contact: {
    title: string;
    subtitle: string;
    sentTitle: string;
    sentBody: string;
    labels: { name: string; email: string; subject: string; message: string };
    placeholders: { name: string; email: string; subject: string; message: string };
    submit: string;
    emailUs: string;
    github: string;
    githubBody: string;
    viewRepo: string;
  };
  privacy: {
    title: string;
    updated: string;
    sections: Array<{ title: string; body?: string; bullets?: string[] }>;
  };
  terms: {
    title: string;
    updated: string;
    sections: Array<{ title: string; body: string; bullets?: string[] }>;
  };
  blog: {
    title: string;
    description: string;
    intro: string;
    featured: string;
    article: string;
    readArticle: string;
    previous: string;
    next: string;
    page: string;
    of: string;
    noTools: string;
    clear: string;
    backToBlog: string;
    by: string;
  };
  toolsPage: {
    title: string;
    description: string;
    intro: string;
    searchPlaceholder: string;
    noResultsTitle: string;
    noResultsBody: string;
    clearSearch: string;
    backToTools: string;
    howToUse: string;
    didYouKnow: string;
    didYouKnowBody: string;
    relatedTools: string;
    whatMakesUseful: string;
    howToOnline: string;
    uploadOrEnter: string;
    configure: string;
    processDownload: string;
    bestUseCases: string;
    whyChoose: string;
    fastFree: string;
    fastFreeBody: string;
    securePrivate: string;
    securePrivateBody: string;
    highQuality: string;
    highQualityBody: string;
    faqTitle: string;
    relatedCategory: string;
    removeFile: string;
    convertedImages: string;
    downloadAll: string;
    downloadPage: string;
    pageLabel: string;
    convertingPdf: string;
    convertingPdfToImages: string;
    complete: string;
    completed: string;
    convertToPng: string;
    convertToPngDescription: string;
    convertToJpg: string;
    convertToJpgDescription: string;
    invalidPdfFile: string;
    pdfToPngError: string;
    pdfConversionError: string;
    convertToWord: string;
    convertToWordDescription: string;
    convertToWordButton: string;
    convertingToWord: string;
    pdfToWordError: string;
    convertToPdf: string;
    convertingToPdf: string;
    wordToPdfError: string;
    mergePdfTitle: string;
    mergePdfDescription: string;
    mergePdfHint: string;
    filesToMerge: string;
    clearAll: string;
    moveUp: string;
    moveDown: string;
    merging: string;
    mergePdfs: string;
    mergePdfError: string;
  };
};

const en: SiteContent = {
  landing: {
    heroTitle: "Master your digital documents with ease",
    heroDescription: "The ultimate collection of free online tools for PDF, images, and text. Secure, fast, and no registration required.",
    heroCta: "Start Converting",
    toolsTitle: "Popular Tools",
    toolsDescription: "Explore our most used free online tools. Secure, fast, and easy to use.",
    toolsCta: "View All Tools",
    useCasesTitle: "Use Cases",
    useCasesDescription: "Discover how Dogufy helps different professionals achieve their goals.",
    useCases: [
      { title: "For Students & Academics", description: "Merge assignments, compress thesis files, and convert research documents into editable formats." },
      { title: "For Business Professionals", description: "Sign contracts, merge reports, and convert spreadsheets into shareable PDF files." },
      { title: "For Designers & Creatives", description: "Optimize images, remove backgrounds, and convert assets faster." },
      { title: "For Legal Teams", description: "Handle sensitive documents with privacy-focused workflows and cleaner document organization." },
      { title: "For Real Estate Agents", description: "Prepare brochures, property files, and listing assets more efficiently." },
    ],
    useCasesCta: "Try Tools",
    processTitle: "How It Works",
    processDescription: "Simple, fast, and secure. Get your documents ready in 4 easy steps.",
    processSteps: [
      { number: "01", title: "Select Your Tool", description: "Choose the right workflow from our collection of PDF, image, and text tools." },
      { number: "02", title: "Upload Your Files", description: "Drop your file directly into the browser and configure the available options." },
      { number: "03", title: "Process in Seconds", description: "Run the task instantly without installing extra software." },
      { number: "04", title: "Download & Share", description: "Get the result immediately and continue your workflow." },
    ],
    featuresTitle: "Why Choose Us",
    featuresDescription: "We provide free online tools built for speed, privacy, and practical workflows.",
    features: [
      { title: "Free Forever", description: "Use the tools without subscriptions, hidden fees, or payment walls." },
      { title: "Secure & Private", description: "Privacy-aware workflows help keep sensitive files under control." },
      { title: "Lightning Fast", description: "Fast browser-based workflows reduce time spent on repetitive tasks." },
      { title: "Easy to Use", description: "The interface is designed to be approachable even for non-technical users." },
      { title: "Mobile Friendly", description: "The experience works across desktop, tablet, and mobile devices." },
      { title: "High Quality", description: "The goal is to preserve useful output quality while keeping the process simple." },
    ],
    testimonialsTitle: "Testimonials",
    testimonialsDescription: "See what users say about Dogufy.",
    testimonials: [
      { text: "Dogufy helped me merge thesis chapters quickly without unnecessary complexity.", author: "Sarah J.", role: "Graduate Student" },
      { text: "I use the image tools regularly for web work because they are fast and convenient.", author: "Mike T.", role: "Frontend Developer" },
      { text: "The document conversion tools save time when preparing editable versions of files.", author: "Emily R.", role: "Legal Assistant" },
    ],
    landingContactTitle: "Contact Us",
    landingContactDescription: "Connect with us if you want to discuss the product, suggest improvements, or ask questions.",
    sayHi: "Say Hi",
    getQuote: "Get a Quote",
    name: "Name",
    email: "Email",
    message: "Message",
    send: "Send Message",
    faqTitle: "FAQ",
    faqDescription: "Common questions about our services and tools.",
    faqs: [
      { question: "Is Dogufy free to use?", answer: "Yes. The core tools are available without subscriptions or hidden fees." },
      { question: "Is my data secure?", answer: "Most workflows process files locally in the browser or temporarily on secure infrastructure." },
      { question: "Do I need an account?", answer: "No account is required for the core experience." },
      { question: "Can I use Dogufy on mobile?", answer: "Yes, the website is designed to work across devices." },
      { question: "What formats are supported?", answer: "Support varies by tool and includes common PDF, image, and text workflows." },
      { question: "Can I process multiple files?", answer: "Many tools support multiple files, depending on the workflow." },
    ],
  },
  about: {
    title: "About Us",
    intro: "Welcome to Dogufy, your one-stop solution for document, image, and text processing needs.",
    missionTitle: "Our Mission",
    missionBody:
      "We believe powerful productivity tools should be accessible to everyone. Our mission is to provide free, high-quality online tools that help users work faster without expensive software or complicated setup.",
    offerTitle: "What We Offer",
    offers: [
      "PDF tools for converting, merging, splitting, compressing, and editing documents.",
      "Image tools for resizing, compressing, converting, and cleaning visual assets.",
      "Text tools for formatting, converting, and analyzing content.",
      "Developer utilities such as QR generation, UUID generation, and other quick helpers.",
    ],
    privacyTitle: "Privacy First",
    privacyBody:
      "Most file processing happens directly in the browser. Your files stay under your control, which helps keep workflows fast, private, and secure.",
    freeTitle: "100% Free",
    freeBody:
      "Our tools are available without subscriptions, hidden fees, or registration barriers. The goal is to make useful utilities broadly accessible.",
  },
  contact: {
    title: "Contact Us",
    subtitle: "We'd love to hear from you.",
    sentTitle: "Message Sent!",
    sentBody: "Thank you for reaching out. We'll get back to you shortly.",
    labels: { name: "Name", email: "Email", subject: "Subject", message: "Message" },
    placeholders: {
      name: "Your name",
      email: "your.email@example.com",
      subject: "What is this about?",
      message: "Tell us more...",
    },
    submit: "Send Message",
    emailUs: "Email Us",
    github: "GitHub",
    githubBody: "Found a bug or want to contribute? Check the repository.",
    viewRepo: "View Repository",
  },
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated",
    sections: [
      { title: "Your Privacy Matters", body: "We take privacy seriously and aim to minimize unnecessary data collection." },
      {
        title: "Data Processing",
        body: "Most tools process files locally in your browser. Some advanced conversions may require temporary server-side processing.",
        bullets: [
          "Files usually stay on your device during processing.",
          "Temporary server-side jobs are deleted after completion.",
          "We do not intentionally store document content.",
        ],
      },
      {
        title: "Information We Collect",
        bullets: [
          "Anonymous usage analytics only after consent.",
          "No account creation is required for core tools.",
          "No permanent file storage for uploaded content.",
        ],
      },
      {
        title: "Cookies",
        bullets: [
          "Consent preference storage.",
          "Security and essential site functionality.",
          "Optional analytics after explicit permission.",
        ],
      },
      {
        title: "Your Rights",
        body: "You can reject analytics, control local storage by clearing browser data, and stop using the service at any time.",
      },
    ],
  },
  terms: {
    title: "Terms of Service",
    updated: "Last updated",
    sections: [
      { title: "1. Acceptance of Terms", body: "By using Dogufy, you agree to these terms." },
      {
        title: "2. Use of Service",
        body: "The service is provided for personal and commercial productivity use.",
        bullets: [
          "Use the service lawfully.",
          "Do not overload, exploit, or abuse the platform.",
          "Do not use the tools for harmful or malicious purposes.",
        ],
      },
      {
        title: "3. Service Availability",
        body: "We aim for reliability but cannot guarantee uninterrupted access or error-free operation.",
      },
      {
        title: "4. Limitation of Liability",
        body: "Use of the service is at your own risk, including file quality checks and workflow suitability.",
      },
      {
        title: "5. User Responsibilities",
        body: "You remain responsible for your files and your right to process them.",
        bullets: [
          "Keep backups of important originals.",
          "Verify output quality before critical use.",
          "Respect copyright and other legal obligations.",
        ],
      },
      {
        title: "6. Contact",
        body: "If you have questions about these terms, please contact us through the website.",
      },
    ],
  },
  blog: {
    title: "Blog",
    description: "Read our latest articles about PDF tools, image processing, and productivity.",
    intro: "Explore practical articles about documents, images, and digital workflows.",
    featured: "Featured",
    article: "Article",
    readArticle: "Read article",
    previous: "Previous",
    next: "Next",
    page: "Page",
    of: "of",
    noTools: "No results found.",
    clear: "Clear Search",
    backToBlog: "Back to Blog",
    by: "by",
  },
  toolsPage: {
    title: "All Tools",
    description: "Browse our complete collection of free online tools. Search to find exactly what you need.",
    intro:
      "Dogufy works best when users can quickly discover the exact workflow they need. This hub helps search engines and visitors understand the full tool library and navigate to the right page.",
    searchPlaceholder: "Search for a tool (e.g. merge, image, pdf)...",
    noResultsTitle: "No tools found",
    noResultsBody: "We couldn't find any tools matching your search. Try another keyword or category.",
    clearSearch: "Clear Search",
    backToTools: "Back to all tools",
    howToUse: "How to use",
    didYouKnow: "Did you know?",
    didYouKnowBody:
      "Many workflows happen directly in the browser, which helps keep document handling faster and more private.",
    relatedTools: "Related tools",
    whatMakesUseful: "What makes this tool useful?",
    howToOnline: "How to use this tool online",
    uploadOrEnter: "Upload or Enter Data",
    configure: "Configure Settings",
    processDownload: "Process & Download",
    bestUseCases: "Best use cases",
    whyChoose: "Why choose Dogufy for this tool?",
    fastFree: "Fast & Free",
    fastFreeBody: "Use the workflow quickly without registration barriers.",
    securePrivate: "Secure & Private",
    securePrivateBody: "The experience is designed to reduce unnecessary exposure of user files.",
    highQuality: "High Quality",
    highQualityBody: "We aim to keep useful output quality while simplifying the workflow.",
    faqTitle: "Frequently Asked Questions",
    relatedCategory: "Related tools",
    removeFile: "Remove file",
    convertedImages: "Converted Images",
    downloadAll: "Download All",
    downloadPage: "Download Page",
    pageLabel: "Page",
    convertingPdf: "Converting PDF...",
    convertingPdfToImages: "Converting PDF to Images...",
    complete: "Complete",
    completed: "completed",
    convertToPng: "Convert to PNG",
    convertToPngDescription: "Convert PDF pages to high-quality PNG images with transparency support.",
    convertToJpg: "Convert to JPG",
    convertToJpgDescription: "Convert PDF pages to high-quality JPG images.",
    invalidPdfFile: "Please select a PDF file.",
    pdfToPngError: "Error converting PDF to PNG",
    pdfConversionError: "Error converting PDF. Please try again.",
    convertToWord: "Convert to Word",
    convertToWordDescription: "Convert your PDF files to editable Word documents (DOCX).",
    convertToWordButton: "Convert to Word (.docx)",
    convertingToWord: "Converting to Word...",
    pdfToWordError: "Error converting PDF to Word. Please try again.",
    convertToPdf: "Convert to PDF",
    convertingToPdf: "Converting to PDF...",
    wordToPdfError: "Error converting Word to PDF. Please try again.",
    mergePdfTitle: "Merge PDF Files",
    mergePdfDescription: "Combine multiple PDF files into a single document. Drag and drop to reorder files.",
    mergePdfHint: "Select 2 or more PDF files to merge",
    filesToMerge: "Files to Merge",
    clearAll: "Clear All",
    moveUp: "Move up",
    moveDown: "Move down",
    merging: "Merging...",
    mergePdfs: "Merge PDFs",
    mergePdfError: "Error merging PDFs. Please make sure all files are valid PDFs.",
  },
};

const id: SiteContent = {
  landing: {
    heroTitle: "Kelola dokumen digital dengan lebih mudah",
    heroDescription: "Kumpulan tools online gratis untuk PDF, gambar, dan teks. Aman, cepat, dan tanpa perlu registrasi.",
    heroCta: "Mulai Konversi",
    toolsTitle: "Tools Populer",
    toolsDescription: "Jelajahi tools gratis yang paling sering digunakan. Aman, cepat, dan mudah dipakai.",
    toolsCta: "Lihat Semua Tools",
    useCasesTitle: "Use Case",
    useCasesDescription: "Lihat bagaimana Dogufy membantu berbagai profesi mencapai tujuan mereka.",
    useCases: [
      { title: "Untuk Pelajar & Akademisi", description: "Gabungkan tugas, kompres file skripsi, dan ubah dokumen riset ke format editable." },
      { title: "Untuk Profesional Bisnis", description: "Tandatangani kontrak, gabungkan laporan, dan ubah spreadsheet menjadi PDF." },
      { title: "Untuk Desainer & Kreator", description: "Optimalkan gambar, hapus background, dan convert aset dengan lebih cepat." },
      { title: "Untuk Tim Legal", description: "Kelola dokumen sensitif dengan workflow yang lebih privat dan terstruktur." },
      { title: "Untuk Agen Properti", description: "Siapkan brosur, dokumen properti, dan aset listing dengan lebih efisien." },
    ],
    useCasesCta: "Coba Tools",
    processTitle: "Cara Kerja",
    processDescription: "Sederhana, cepat, dan aman. Selesaikan dokumen kamu dalam 4 langkah mudah.",
    processSteps: [
      { number: "01", title: "Pilih Tools", description: "Pilih workflow yang tepat dari koleksi tools PDF, gambar, dan teks." },
      { number: "02", title: "Upload File", description: "Masukkan file langsung ke browser dan atur opsi yang tersedia." },
      { number: "03", title: "Proses Dalam Hitungan Detik", description: "Jalankan tugas tanpa perlu install software tambahan." },
      { number: "04", title: "Unduh & Bagikan", description: "Dapatkan hasilnya langsung dan lanjutkan workflow kamu." },
    ],
    featuresTitle: "Kenapa Memilih Kami",
    featuresDescription: "Kami menyediakan tools online gratis yang dibuat untuk kecepatan, privasi, dan workflow yang praktis.",
    features: [
      { title: "Gratis Selamanya", description: "Gunakan tools tanpa langganan, biaya tersembunyi, atau paywall." },
      { title: "Aman & Privat", description: "Workflow yang sadar privasi membantu menjaga file sensitif tetap terkendali." },
      { title: "Sangat Cepat", description: "Workflow berbasis browser membantu menghemat waktu untuk tugas berulang." },
      { title: "Mudah Dipakai", description: "Tampilan dirancang agar nyaman digunakan bahkan oleh pengguna non-teknis." },
      { title: "Ramah Mobile", description: "Pengalaman tetap nyaman di desktop, tablet, maupun mobile." },
      { title: "Kualitas Baik", description: "Fokus kami adalah menjaga kualitas hasil sambil tetap membuat prosesnya sederhana." },
    ],
    testimonialsTitle: "Testimoni",
    testimonialsDescription: "Lihat apa kata pengguna tentang Dogufy.",
    testimonials: [
      { text: "Dogufy membantu saya menggabungkan bab skripsi dengan cepat tanpa ribet.", author: "Sarah J.", role: "Mahasiswa Pascasarjana" },
      { text: "Saya sering memakai image tools untuk pekerjaan web karena cepat dan praktis.", author: "Mike T.", role: "Frontend Developer" },
      { text: "Tools konversi dokumennya menghemat waktu saat menyiapkan file editable.", author: "Emily R.", role: "Asisten Legal" },
    ],
    landingContactTitle: "Hubungi Kami",
    landingContactDescription: "Hubungi kami jika ingin berdiskusi tentang produk, memberi saran, atau mengajukan pertanyaan.",
    sayHi: "Sapa Kami",
    getQuote: "Minta Penawaran",
    name: "Nama",
    email: "Email",
    message: "Pesan",
    send: "Kirim Pesan",
    faqTitle: "FAQ",
    faqDescription: "Pertanyaan umum tentang layanan dan tools kami.",
    faqs: [
      { question: "Apakah Dogufy gratis?", answer: "Ya. Tools inti tersedia tanpa langganan atau biaya tersembunyi." },
      { question: "Apakah data saya aman?", answer: "Sebagian besar workflow memproses file secara lokal di browser atau sementara di infrastruktur yang aman." },
      { question: "Apakah saya perlu akun?", answer: "Tidak perlu akun untuk pengalaman inti." },
      { question: "Apakah Dogufy bisa dipakai di mobile?", answer: "Ya, website ini dirancang agar berjalan di berbagai perangkat." },
      { question: "Format apa saja yang didukung?", answer: "Dukungan format tergantung tools dan mencakup workflow umum PDF, gambar, dan teks." },
      { question: "Apakah bisa memproses banyak file sekaligus?", answer: "Banyak tools mendukung banyak file, tergantung jenis workflow-nya." },
    ],
  },
  about: {
    title: "Tentang Kami",
    intro: "Selamat datang di Dogufy, solusi praktis untuk kebutuhan dokumen, gambar, dan teks kamu.",
    missionTitle: "Misi Kami",
    missionBody:
      "Kami percaya tools produktivitas yang kuat harus bisa diakses semua orang. Misi kami adalah menyediakan tools online gratis berkualitas tinggi yang membantu pengguna bekerja lebih cepat tanpa software mahal atau setup yang rumit.",
    offerTitle: "Apa yang Kami Tawarkan",
    offers: [
      "Tools PDF untuk convert, merge, split, compress, dan edit dokumen.",
      "Tools gambar untuk resize, compress, convert, dan membersihkan aset visual.",
      "Tools teks untuk formatting, converting, dan analisis konten.",
      "Utility developer seperti generator QR, generator UUID, dan helper cepat lainnya.",
    ],
    privacyTitle: "Privasi Diutamakan",
    privacyBody:
      "Sebagian besar pemrosesan file terjadi langsung di browser. File tetap dalam kontrol kamu agar workflow tetap cepat, privat, dan aman.",
    freeTitle: "100% Gratis",
    freeBody:
      "Semua tools tersedia tanpa langganan, biaya tersembunyi, atau kewajiban registrasi. Tujuannya adalah membuat utilitas yang berguna dapat diakses lebih luas.",
  },
  contact: {
    title: "Hubungi Kami",
    subtitle: "Kami senang mendengar dari kamu.",
    sentTitle: "Pesan Terkirim!",
    sentBody: "Terima kasih sudah menghubungi kami. Kami akan segera membalas.",
    labels: { name: "Nama", email: "Email", subject: "Subjek", message: "Pesan" },
    placeholders: {
      name: "Nama kamu",
      email: "email@contoh.com",
      subject: "Tentang apa ini?",
      message: "Ceritakan lebih detail...",
    },
    submit: "Kirim Pesan",
    emailUs: "Kirim Email",
    github: "GitHub",
    githubBody: "Menemukan bug atau ingin berkontribusi? Lihat repository kami.",
    viewRepo: "Lihat Repository",
  },
  privacy: {
    title: "Kebijakan Privasi",
    updated: "Terakhir diperbarui",
    sections: [
      { title: "Privasi Kamu Penting", body: "Kami menjaga privasi dengan serius dan berusaha meminimalkan pengumpulan data yang tidak perlu." },
      {
        title: "Pemrosesan Data",
        body: "Sebagian besar tools memproses file secara lokal di browser. Beberapa konversi lanjutan mungkin memerlukan pemrosesan sementara di server.",
        bullets: [
          "File umumnya tetap di perangkat kamu saat diproses.",
          "Job sementara di server dihapus setelah selesai.",
          "Kami tidak menyimpan isi dokumen secara sengaja.",
        ],
      },
      {
        title: "Informasi Yang Kami Kumpulkan",
        bullets: [
          "Analitik anonim hanya setelah ada persetujuan.",
          "Tidak perlu membuat akun untuk memakai tools inti.",
          "Tidak ada penyimpanan permanen untuk file yang diunggah.",
        ],
      },
      {
        title: "Cookie",
        bullets: [
          "Penyimpanan preferensi consent.",
          "Keamanan dan fungsi penting website.",
          "Analitik opsional setelah izin diberikan.",
        ],
      },
      {
        title: "Hak Kamu",
        body: "Kamu bisa menolak analitik, menghapus data lokal lewat browser, dan berhenti menggunakan layanan kapan saja.",
      },
    ],
  },
  terms: {
    title: "Syarat Layanan",
    updated: "Terakhir diperbarui",
    sections: [
      { title: "1. Penerimaan Syarat", body: "Dengan menggunakan Dogufy, kamu menyetujui syarat ini." },
      {
        title: "2. Penggunaan Layanan",
        body: "Layanan disediakan untuk kebutuhan produktivitas pribadi maupun komersial.",
        bullets: [
          "Gunakan layanan secara sah.",
          "Jangan membebani, mengeksploitasi, atau menyalahgunakan platform.",
          "Jangan gunakan tools untuk tujuan yang berbahaya atau melanggar hukum.",
        ],
      },
      {
        title: "3. Ketersediaan Layanan",
        body: "Kami berupaya menjaga keandalan, tetapi tidak dapat menjamin akses tanpa gangguan atau bebas error.",
      },
      {
        title: "4. Batasan Tanggung Jawab",
        body: "Penggunaan layanan sepenuhnya menjadi tanggung jawab pengguna, termasuk pengecekan kualitas file hasil dan kesesuaian workflow.",
      },
      {
        title: "5. Tanggung Jawab Pengguna",
        body: "Kamu tetap bertanggung jawab atas file dan hak kamu untuk memprosesnya.",
        bullets: [
          "Simpan backup file penting.",
          "Periksa kualitas hasil sebelum dipakai untuk kebutuhan penting.",
          "Patuhi hak cipta dan kewajiban hukum lainnya.",
        ],
      },
      {
        title: "6. Kontak",
        body: "Jika ada pertanyaan tentang syarat ini, silakan hubungi kami melalui website.",
      },
    ],
  },
  blog: {
    title: "Blog",
    description: "Baca artikel terbaru kami tentang tools PDF, pemrosesan gambar, dan produktivitas.",
    intro: "Jelajahi artikel praktis tentang dokumen, gambar, dan workflow digital.",
    featured: "Unggulan",
    article: "Artikel",
    readArticle: "Baca artikel",
    previous: "Sebelumnya",
    next: "Berikutnya",
    page: "Halaman",
    of: "dari",
    noTools: "Tidak ada hasil ditemukan.",
    clear: "Hapus pencarian",
    backToBlog: "Kembali ke Blog",
    by: "oleh",
  },
  toolsPage: {
    title: "Semua Tools",
    description: "Jelajahi koleksi lengkap tools online gratis kami. Cari tools yang tepat sesuai kebutuhan.",
    intro:
      "Dogufy akan lebih berguna saat pengguna bisa cepat menemukan workflow yang tepat. Halaman ini membantu search engine dan pengunjung memahami seluruh library tools dan menuju halaman yang sesuai.",
    searchPlaceholder: "Cari tools (misalnya merge, image, pdf)...",
    noResultsTitle: "Tools tidak ditemukan",
    noResultsBody: "Kami tidak menemukan tools yang cocok dengan pencarian kamu. Coba kata kunci atau kategori lain.",
    clearSearch: "Hapus Pencarian",
    backToTools: "Kembali ke semua tools",
    howToUse: "Cara pakai",
    didYouKnow: "Tahukah kamu?",
    didYouKnowBody:
      "Banyak workflow berjalan langsung di browser sehingga penanganan dokumen bisa lebih cepat dan lebih privat.",
    relatedTools: "Tools terkait",
    whatMakesUseful: "Apa yang membuat tool ini berguna?",
    howToOnline: "Cara memakai tool ini secara online",
    uploadOrEnter: "Upload atau Masukkan Data",
    configure: "Atur Pengaturan",
    processDownload: "Proses & Unduh",
    bestUseCases: "Use case terbaik",
    whyChoose: "Kenapa memilih Dogufy untuk tool ini?",
    fastFree: "Cepat & Gratis",
    fastFreeBody: "Gunakan workflow ini dengan cepat tanpa hambatan registrasi.",
    securePrivate: "Aman & Privat",
    securePrivateBody: "Pengalaman ini dirancang untuk mengurangi paparan file pengguna yang tidak perlu.",
    highQuality: "Kualitas Baik",
    highQualityBody: "Kami berusaha menjaga kualitas hasil sambil menyederhanakan proses.",
    faqTitle: "Pertanyaan yang Sering Diajukan",
    relatedCategory: "Tools terkait",
    removeFile: "Hapus file",
    convertedImages: "Gambar Hasil Konversi",
    downloadAll: "Unduh Semua",
    downloadPage: "Unduh Halaman",
    pageLabel: "Halaman",
    convertingPdf: "Mengonversi PDF...",
    convertingPdfToImages: "Mengonversi PDF ke gambar...",
    complete: "Selesai",
    completed: "selesai",
    convertToPng: "Konversi ke PNG",
    convertToPngDescription: "Ubah setiap halaman PDF menjadi gambar PNG berkualitas tinggi dengan dukungan transparansi.",
    convertToJpg: "Konversi ke JPG",
    convertToJpgDescription: "Ubah setiap halaman PDF menjadi gambar JPG berkualitas tinggi.",
    invalidPdfFile: "Silakan pilih file PDF.",
    pdfToPngError: "Terjadi kesalahan saat mengonversi PDF ke PNG",
    pdfConversionError: "Terjadi kesalahan saat mengonversi PDF. Silakan coba lagi.",
    convertToWord: "Konversi ke Word",
    convertToWordDescription: "Ubah file PDF kamu menjadi dokumen Word yang bisa diedit (DOCX).",
    convertToWordButton: "Konversi ke Word (.docx)",
    convertingToWord: "Mengonversi ke Word...",
    pdfToWordError: "Terjadi kesalahan saat mengonversi PDF ke Word. Silakan coba lagi.",
    convertToPdf: "Konversi ke PDF",
    convertingToPdf: "Mengonversi ke PDF...",
    wordToPdfError: "Terjadi kesalahan saat mengonversi Word ke PDF. Silakan coba lagi.",
    mergePdfTitle: "Gabungkan File PDF",
    mergePdfDescription: "Gabungkan beberapa file PDF menjadi satu dokumen. Seret dan lepas untuk mengatur ulang urutan file.",
    mergePdfHint: "Pilih 2 file PDF atau lebih untuk digabungkan",
    filesToMerge: "File untuk Digabungkan",
    clearAll: "Hapus Semua",
    moveUp: "Pindah ke atas",
    moveDown: "Pindah ke bawah",
    merging: "Menggabungkan...",
    mergePdfs: "Gabungkan PDF",
    mergePdfError: "Terjadi kesalahan saat menggabungkan PDF. Pastikan semua file adalah PDF yang valid.",
  },
};

export function getSiteContent(locale: Locale): SiteContent {
  if (locale === "id") return id;

  const override = localeOverrides[locale];
  if (override) {
    const base = mergeSiteContent(en, override);
    const completion = siteContentCompletions[locale] as DeepPartial<SiteContent> | undefined;
    return completion ? mergeSiteContent(base, completion) : base;
  }

  const completion = siteContentCompletions[locale] as DeepPartial<SiteContent> | undefined;
  return completion ? mergeSiteContent(en, completion) : en;
}

const localeOverrides: Partial<Record<Locale, DeepPartial<SiteContent>>> = {
  vi: {
    landing: {
      heroTitle: "Làm chủ tài liệu số một cách dễ dàng",
      heroDescription: "Bộ công cụ trực tuyến miễn phí cho PDF, hình ảnh và văn bản. An toàn, nhanh chóng và không cần đăng ký.",
      heroCta: "Bắt đầu chuyển đổi",
      toolsTitle: "Công cụ phổ biến",
      toolsDescription: "Khám phá những công cụ trực tuyến miễn phí được dùng nhiều nhất.",
      toolsCta: "Xem tất cả công cụ",
      processTitle: "Cách hoạt động",
      featuresTitle: "Vì sao chọn chúng tôi",
      testimonialsTitle: "Đánh giá",
      landingContactTitle: "Liên hệ",
      faqTitle: "FAQ",
    },
    about: { title: "Về chúng tôi", missionTitle: "Sứ mệnh của chúng tôi", offerTitle: "Những gì chúng tôi cung cấp" },
    contact: { title: "Liên hệ", subtitle: "Chúng tôi rất mong nhận được phản hồi từ bạn.", submit: "Gửi tin nhắn", emailUs: "Gửi email" },
    privacy: { title: "Chính sách quyền riêng tư", updated: "Cập nhật lần cuối" },
    terms: { title: "Điều khoản dịch vụ", updated: "Cập nhật lần cuối" },
    blog: { title: "Blog", featured: "Nổi bật", article: "Bài viết", readArticle: "Đọc bài viết", previous: "Trước", next: "Tiếp theo", page: "Trang", of: "trên", backToBlog: "Quay lại blog", by: "bởi" },
    toolsPage: { title: "Tất cả công cụ", description: "Duyệt toàn bộ bộ sưu tập công cụ trực tuyến miễn phí của chúng tôi.", clearSearch: "Xóa tìm kiếm", backToTools: "Quay lại tất cả công cụ", howToUse: "Cách sử dụng", relatedTools: "Công cụ liên quan", faqTitle: "Câu hỏi thường gặp" },
  },
  cs: {
    landing: { heroTitle: "Ovládejte své digitální dokumenty snadno", heroDescription: "Bezplatné online nástroje pro PDF, obrázky a text. Bezpečné, rychlé a bez registrace.", heroCta: "Začít převádět", toolsTitle: "Populární nástroje", toolsCta: "Zobrazit všechny nástroje", processTitle: "Jak to funguje", featuresTitle: "Proč si vybrat nás", testimonialsTitle: "Reference", landingContactTitle: "Kontaktujte nás", faqTitle: "FAQ" },
    about: { title: "O nás", missionTitle: "Naše mise", offerTitle: "Co nabízíme" },
    contact: { title: "Kontaktujte nás", subtitle: "Rádi od vás uslyšíme.", submit: "Odeslat zprávu", emailUs: "Napište nám" },
    privacy: { title: "Zásady ochrany osobních údajů", updated: "Naposledy aktualizováno" },
    terms: { title: "Podmínky služby", updated: "Naposledy aktualizováno" },
    blog: { title: "Blog", featured: "Doporučené", article: "Článek", readArticle: "Číst článek", previous: "Předchozí", next: "Další", page: "Stránka", of: "z", backToBlog: "Zpět na blog", by: "od" },
    toolsPage: { title: "Všechny nástroje", clearSearch: "Vymazat hledání", backToTools: "Zpět na všechny nástroje", howToUse: "Jak používat", relatedTools: "Související nástroje", faqTitle: "Často kladené otázky" },
  },
  es: {
    landing: { heroTitle: "Domina tus documentos digitales fácilmente", heroDescription: "La colección definitiva de herramientas online gratis para PDF, imágenes y texto.", heroCta: "Empezar a convertir", toolsTitle: "Herramientas populares", toolsCta: "Ver todas las herramientas", processTitle: "Cómo funciona", featuresTitle: "Por qué elegirnos", testimonialsTitle: "Testimonios", landingContactTitle: "Contáctanos", faqTitle: "FAQ" },
    about: { title: "Sobre nosotros", missionTitle: "Nuestra misión", offerTitle: "Qué ofrecemos" },
    contact: { title: "Contáctanos", subtitle: "Nos encantará saber de ti.", submit: "Enviar mensaje", emailUs: "Envíanos un correo" },
    privacy: { title: "Política de privacidad", updated: "Última actualización" },
    terms: { title: "Términos del servicio", updated: "Última actualización" },
    blog: { title: "Blog", featured: "Destacado", article: "Artículo", readArticle: "Leer artículo", previous: "Anterior", next: "Siguiente", page: "Página", of: "de", backToBlog: "Volver al blog", by: "por" },
    toolsPage: { title: "Todas las herramientas", clearSearch: "Limpiar búsqueda", backToTools: "Volver a todas las herramientas", howToUse: "Cómo usar", relatedTools: "Herramientas relacionadas", faqTitle: "Preguntas frecuentes" },
  },
  fr: {
    landing: { heroTitle: "Maîtrisez vos documents numériques facilement", heroDescription: "La collection ultime d'outils en ligne gratuits pour PDF, images et texte.", heroCta: "Commencer la conversion", toolsTitle: "Outils populaires", toolsCta: "Voir tous les outils", processTitle: "Comment ça marche", featuresTitle: "Pourquoi nous choisir", testimonialsTitle: "Témoignages", landingContactTitle: "Contactez-nous", faqTitle: "FAQ" },
    about: { title: "À propos", missionTitle: "Notre mission", offerTitle: "Ce que nous proposons" },
    contact: { title: "Contactez-nous", subtitle: "Nous serions ravis d'avoir de vos nouvelles.", submit: "Envoyer le message", emailUs: "Nous écrire" },
    privacy: { title: "Politique de confidentialité", updated: "Dernière mise à jour" },
    terms: { title: "Conditions d'utilisation", updated: "Dernière mise à jour" },
    blog: { title: "Blog", featured: "À la une", article: "Article", readArticle: "Lire l'article", previous: "Précédent", next: "Suivant", page: "Page", of: "sur", backToBlog: "Retour au blog", by: "par" },
    toolsPage: { title: "Tous les outils", clearSearch: "Effacer la recherche", backToTools: "Retour à tous les outils", howToUse: "Comment utiliser", relatedTools: "Outils associés", faqTitle: "Questions fréquentes" },
  },
  de: {
    landing: { heroTitle: "Beherrsche deine digitalen Dokumente mit Leichtigkeit", heroDescription: "Die ultimative Sammlung kostenloser Online-Tools für PDF, Bilder und Text.", heroCta: "Konvertierung starten", toolsTitle: "Beliebte Tools", toolsCta: "Alle Tools ansehen", processTitle: "So funktioniert es", featuresTitle: "Warum wir", testimonialsTitle: "Bewertungen", landingContactTitle: "Kontakt", faqTitle: "FAQ" },
    about: { title: "Über uns", missionTitle: "Unsere Mission", offerTitle: "Was wir anbieten" },
    contact: { title: "Kontakt", subtitle: "Wir freuen uns auf deine Nachricht.", submit: "Nachricht senden", emailUs: "E-Mail senden" },
    privacy: { title: "Datenschutzerklärung", updated: "Zuletzt aktualisiert" },
    terms: { title: "Nutzungsbedingungen", updated: "Zuletzt aktualisiert" },
    blog: { title: "Blog", featured: "Empfohlen", article: "Artikel", readArticle: "Artikel lesen", previous: "Zurück", next: "Weiter", page: "Seite", of: "von", backToBlog: "Zurück zum Blog", by: "von" },
    toolsPage: { title: "Alle Tools", clearSearch: "Suche löschen", backToTools: "Zurück zu allen Tools", howToUse: "So verwendest du es", relatedTools: "Ähnliche Tools", faqTitle: "Häufige Fragen" },
  },
  it: {
    landing: { heroTitle: "Gestisci i tuoi documenti digitali con facilità", heroDescription: "La raccolta definitiva di strumenti online gratuiti per PDF, immagini e testo.", heroCta: "Inizia a convertire", toolsTitle: "Strumenti popolari", toolsCta: "Vedi tutti gli strumenti", processTitle: "Come funziona", featuresTitle: "Perché sceglierci", testimonialsTitle: "Testimonianze", landingContactTitle: "Contattaci", faqTitle: "FAQ" },
    about: { title: "Chi siamo", missionTitle: "La nostra missione", offerTitle: "Cosa offriamo" },
    contact: { title: "Contattaci", subtitle: "Ci farebbe piacere sentirti.", submit: "Invia messaggio", emailUs: "Scrivici" },
    privacy: { title: "Informativa sulla privacy", updated: "Ultimo aggiornamento" },
    terms: { title: "Termini di servizio", updated: "Ultimo aggiornamento" },
    blog: { title: "Blog", featured: "In evidenza", article: "Articolo", readArticle: "Leggi articolo", previous: "Precedente", next: "Successivo", page: "Pagina", of: "di", backToBlog: "Torna al blog", by: "di" },
    toolsPage: { title: "Tutti gli strumenti", clearSearch: "Cancella ricerca", backToTools: "Torna a tutti gli strumenti", howToUse: "Come usare", relatedTools: "Strumenti correlati", faqTitle: "Domande frequenti" },
  },
  hu: {
    landing: { heroTitle: "Kezeld digitális dokumentumaidat könnyedén", heroDescription: "Ingyenes online eszközök PDF-hez, képekhez és szövegekhez.", heroCta: "Konvertálás indítása", toolsTitle: "Népszerű eszközök", toolsCta: "Összes eszköz megtekintése", processTitle: "Hogyan működik", featuresTitle: "Miért minket válassz", testimonialsTitle: "Vélemények", landingContactTitle: "Kapcsolat", faqTitle: "GYIK" },
    about: { title: "Rólunk", missionTitle: "Küldetésünk", offerTitle: "Mit kínálunk" },
    contact: { title: "Kapcsolat", subtitle: "Szívesen hallunk felőled.", submit: "Üzenet küldése", emailUs: "Írj nekünk" },
    privacy: { title: "Adatvédelmi szabályzat", updated: "Utolsó frissítés" },
    terms: { title: "Szolgáltatási feltételek", updated: "Utolsó frissítés" },
    blog: { title: "Blog", featured: "Kiemelt", article: "Cikk", readArticle: "Cikk olvasása", previous: "Előző", next: "Következő", page: "Oldal", of: "/", backToBlog: "Vissza a bloghoz", by: "írta" },
    toolsPage: { title: "Összes eszköz", clearSearch: "Keresés törlése", backToTools: "Vissza az összes eszközhöz", howToUse: "Használat", relatedTools: "Kapcsolódó eszközök", faqTitle: "Gyakori kérdések" },
  },
  ms: {
    landing: { heroTitle: "Urus dokumen digital anda dengan mudah", heroDescription: "Koleksi alat dalam talian percuma untuk PDF, imej dan teks.", heroCta: "Mula menukar", toolsTitle: "Alat popular", toolsCta: "Lihat semua alat", processTitle: "Cara ia berfungsi", featuresTitle: "Mengapa pilih kami", testimonialsTitle: "Testimoni", landingContactTitle: "Hubungi kami", faqTitle: "FAQ" },
    about: { title: "Tentang kami", missionTitle: "Misi kami", offerTitle: "Apa yang kami tawarkan" },
    contact: { title: "Hubungi kami", subtitle: "Kami suka mendengar daripada anda.", submit: "Hantar mesej", emailUs: "Emel kami" },
    privacy: { title: "Dasar privasi", updated: "Dikemas kini terakhir" },
    terms: { title: "Terma perkhidmatan", updated: "Dikemas kini terakhir" },
    blog: { title: "Blog", featured: "Utama", article: "Artikel", readArticle: "Baca artikel", previous: "Sebelumnya", next: "Seterusnya", page: "Halaman", of: "daripada", backToBlog: "Kembali ke blog", by: "oleh" },
    toolsPage: { title: "Semua alat", clearSearch: "Padam carian", backToTools: "Kembali ke semua alat", howToUse: "Cara guna", relatedTools: "Alat berkaitan", faqTitle: "Soalan lazim" },
  },
  nl: {
    landing: { heroTitle: "Beheer je digitale documenten eenvoudig", heroDescription: "De ultieme verzameling gratis online tools voor PDF, afbeeldingen en tekst.", heroCta: "Begin met converteren", toolsTitle: "Populaire tools", toolsCta: "Bekijk alle tools", processTitle: "Hoe het werkt", featuresTitle: "Waarom voor ons kiezen", testimonialsTitle: "Testimonials", landingContactTitle: "Neem contact op", faqTitle: "FAQ" },
    about: { title: "Over ons", missionTitle: "Onze missie", offerTitle: "Wat we aanbieden" },
    contact: { title: "Neem contact op", subtitle: "We horen graag van je.", submit: "Bericht verzenden", emailUs: "Mail ons" },
    privacy: { title: "Privacybeleid", updated: "Laatst bijgewerkt" },
    terms: { title: "Servicevoorwaarden", updated: "Laatst bijgewerkt" },
    blog: { title: "Blog", featured: "Uitgelicht", article: "Artikel", readArticle: "Artikel lezen", previous: "Vorige", next: "Volgende", page: "Pagina", of: "van", backToBlog: "Terug naar blog", by: "door" },
    toolsPage: { title: "Alle tools", clearSearch: "Zoekopdracht wissen", backToTools: "Terug naar alle tools", howToUse: "Hoe te gebruiken", relatedTools: "Gerelateerde tools", faqTitle: "Veelgestelde vragen" },
  },
  pl: {
    landing: { heroTitle: "Zarządzaj dokumentami cyfrowymi z łatwością", heroDescription: "Bezpłatne narzędzia online do PDF, obrazów i tekstu.", heroCta: "Rozpocznij konwersję", toolsTitle: "Popularne narzędzia", toolsCta: "Zobacz wszystkie narzędzia", processTitle: "Jak to działa", featuresTitle: "Dlaczego my", testimonialsTitle: "Opinie", landingContactTitle: "Skontaktuj się z nami", faqTitle: "FAQ" },
    about: { title: "O nas", missionTitle: "Nasza misja", offerTitle: "Co oferujemy" },
    contact: { title: "Skontaktuj się z nami", subtitle: "Chętnie się z Tobą skontaktujemy.", submit: "Wyślij wiadomość", emailUs: "Napisz do nas" },
    privacy: { title: "Polityka prywatności", updated: "Ostatnia aktualizacja" },
    terms: { title: "Warunki korzystania", updated: "Ostatnia aktualizacja" },
    blog: { title: "Blog", featured: "Wyróżnione", article: "Artykuł", readArticle: "Czytaj artykuł", previous: "Poprzedni", next: "Następny", page: "Strona", of: "z", backToBlog: "Powrót do bloga", by: "autor" },
    toolsPage: { title: "Wszystkie narzędzia", clearSearch: "Wyczyść wyszukiwanie", backToTools: "Powrót do wszystkich narzędzi", howToUse: "Jak używać", relatedTools: "Powiązane narzędzia", faqTitle: "Najczęstsze pytania" },
  },
  pt: {
    landing: { heroTitle: "Domine os seus documentos digitais com facilidade", heroDescription: "A coleção definitiva de ferramentas online gratuitas para PDF, imagens e texto.", heroCta: "Começar a converter", toolsTitle: "Ferramentas populares", toolsCta: "Ver todas as ferramentas", processTitle: "Como funciona", featuresTitle: "Porque nos escolher", testimonialsTitle: "Testemunhos", landingContactTitle: "Contacte-nos", faqTitle: "FAQ" },
    about: { title: "Sobre nós", missionTitle: "A nossa missão", offerTitle: "O que oferecemos" },
    contact: { title: "Contacte-nos", subtitle: "Gostaríamos de ouvir a sua opinião.", submit: "Enviar mensagem", emailUs: "Enviar e-mail" },
    privacy: { title: "Política de privacidade", updated: "Última atualização" },
    terms: { title: "Termos de serviço", updated: "Última atualização" },
    blog: { title: "Blog", featured: "Destaque", article: "Artigo", readArticle: "Ler artigo", previous: "Anterior", next: "Seguinte", page: "Página", of: "de", backToBlog: "Voltar ao blog", by: "por" },
    toolsPage: { title: "Todas as ferramentas", clearSearch: "Limpar pesquisa", backToTools: "Voltar a todas as ferramentas", howToUse: "Como usar", relatedTools: "Ferramentas relacionadas", faqTitle: "Perguntas frequentes" },
  },
  th: {
    landing: { heroTitle: "จัดการเอกสารดิจิทัลของคุณได้อย่างง่ายดาย", heroDescription: "รวมเครื่องมือออนไลน์ฟรีสำหรับ PDF รูปภาพ และข้อความ", heroCta: "เริ่มแปลงไฟล์", toolsTitle: "เครื่องมือยอดนิยม", toolsCta: "ดูเครื่องมือทั้งหมด", processTitle: "วิธีการทำงาน", featuresTitle: "ทำไมต้องเลือกเรา", testimonialsTitle: "เสียงจากผู้ใช้", landingContactTitle: "ติดต่อเรา", faqTitle: "FAQ" },
    about: { title: "เกี่ยวกับเรา", missionTitle: "พันธกิจของเรา", offerTitle: "สิ่งที่เรามีให้" },
    contact: { title: "ติดต่อเรา", subtitle: "เรายินดีรับฟังจากคุณ", submit: "ส่งข้อความ", emailUs: "ส่งอีเมลหาเรา" },
    privacy: { title: "นโยบายความเป็นส่วนตัว", updated: "อัปเดตล่าสุด" },
    terms: { title: "ข้อกำหนดการให้บริการ", updated: "อัปเดตล่าสุด" },
    blog: { title: "บล็อก", featured: "แนะนำ", article: "บทความ", readArticle: "อ่านบทความ", previous: "ก่อนหน้า", next: "ถัดไป", page: "หน้า", of: "จาก", backToBlog: "กลับไปที่บล็อก", by: "โดย" },
    toolsPage: { title: "เครื่องมือทั้งหมด", clearSearch: "ล้างการค้นหา", backToTools: "กลับไปยังเครื่องมือทั้งหมด", howToUse: "วิธีใช้งาน", relatedTools: "เครื่องมือที่เกี่ยวข้อง", faqTitle: "คำถามที่พบบ่อย" },
  },
  tr: {
    landing: { heroTitle: "Dijital belgelerinizi kolayca yönetin", heroDescription: "PDF, görseller ve metin için ücretsiz çevrimiçi araçlar.", heroCta: "Dönüştürmeye başla", toolsTitle: "Popüler araçlar", toolsCta: "Tüm araçları gör", processTitle: "Nasıl çalışır", featuresTitle: "Neden bizi seçmelisiniz", testimonialsTitle: "Yorumlar", landingContactTitle: "Bize ulaşın", faqTitle: "SSS" },
    about: { title: "Hakkımızda", missionTitle: "Misyonumuz", offerTitle: "Neler sunuyoruz" },
    contact: { title: "Bize ulaşın", subtitle: "Sizden haber almak isteriz.", submit: "Mesaj gönder", emailUs: "Bize e-posta gönder" },
    privacy: { title: "Gizlilik politikası", updated: "Son güncelleme" },
    terms: { title: "Hizmet şartları", updated: "Son güncelleme" },
    blog: { title: "Blog", featured: "Öne çıkan", article: "Makale", readArticle: "Makaleyi oku", previous: "Önceki", next: "Sonraki", page: "Sayfa", of: "/", backToBlog: "Bloga dön", by: "yazan" },
    toolsPage: { title: "Tüm araçlar", clearSearch: "Aramayı temizle", backToTools: "Tüm araçlara dön", howToUse: "Nasıl kullanılır", relatedTools: "İlgili araçlar", faqTitle: "Sık sorulan sorular" },
  },
  el: {
    landing: { heroTitle: "Διαχειριστείτε τα ψηφιακά σας έγγραφα εύκολα", heroDescription: "Δωρεάν online εργαλεία για PDF, εικόνες και κείμενο.", heroCta: "Ξεκινήστε τη μετατροπή", toolsTitle: "Δημοφιλή εργαλεία", toolsCta: "Δείτε όλα τα εργαλεία", processTitle: "Πώς λειτουργεί", featuresTitle: "Γιατί να μας επιλέξετε", testimonialsTitle: "Μαρτυρίες", landingContactTitle: "Επικοινωνήστε μαζί μας", faqTitle: "FAQ" },
    about: { title: "Σχετικά με εμάς", missionTitle: "Η αποστολή μας", offerTitle: "Τι προσφέρουμε" },
    contact: { title: "Επικοινωνία", subtitle: "Θα χαρούμε να σας ακούσουμε.", submit: "Αποστολή μηνύματος", emailUs: "Στείλτε μας email" },
    privacy: { title: "Πολιτική απορρήτου", updated: "Τελευταία ενημέρωση" },
    terms: { title: "Όροι υπηρεσίας", updated: "Τελευταία ενημέρωση" },
    blog: { title: "Blog", featured: "Προτεινόμενο", article: "Άρθρο", readArticle: "Διαβάστε το άρθρο", previous: "Προηγούμενο", next: "Επόμενο", page: "Σελίδα", of: "από", backToBlog: "Πίσω στο blog", by: "από" },
    toolsPage: { title: "Όλα τα εργαλεία", clearSearch: "Καθαρισμός αναζήτησης", backToTools: "Πίσω σε όλα τα εργαλεία", howToUse: "Πώς να το χρησιμοποιήσετε", relatedTools: "Σχετικά εργαλεία", faqTitle: "Συχνές ερωτήσεις" },
  },
  uk: {
    landing: { heroTitle: "Керуйте цифровими документами легко", heroDescription: "Безкоштовні онлайн-інструменти для PDF, зображень і тексту.", heroCta: "Почати конвертацію", toolsTitle: "Популярні інструменти", toolsCta: "Переглянути всі інструменти", processTitle: "Як це працює", featuresTitle: "Чому обирають нас", testimonialsTitle: "Відгуки", landingContactTitle: "Зв'яжіться з нами", faqTitle: "FAQ" },
    about: { title: "Про нас", missionTitle: "Наша місія", offerTitle: "Що ми пропонуємо" },
    contact: { title: "Зв'яжіться з нами", subtitle: "Нам буде приємно вас почути.", submit: "Надіслати повідомлення", emailUs: "Написати нам" },
    privacy: { title: "Політика конфіденційності", updated: "Останнє оновлення" },
    terms: { title: "Умови користування", updated: "Останнє оновлення" },
    blog: { title: "Блог", featured: "Рекомендоване", article: "Стаття", readArticle: "Читати статтю", previous: "Попередній", next: "Наступний", page: "Сторінка", of: "з", backToBlog: "Назад до блогу", by: "автор" },
    toolsPage: { title: "Усі інструменти", clearSearch: "Очистити пошук", backToTools: "Назад до всіх інструментів", howToUse: "Як користуватися", relatedTools: "Пов'язані інструменти", faqTitle: "Поширені запитання" },
  },
  ru: {
    landing: { heroTitle: "Управляйте цифровыми документами легко", heroDescription: "Бесплатные онлайн-инструменты для PDF, изображений и текста.", heroCta: "Начать конвертацию", toolsTitle: "Популярные инструменты", toolsCta: "Посмотреть все инструменты", processTitle: "Как это работает", featuresTitle: "Почему выбирают нас", testimonialsTitle: "Отзывы", landingContactTitle: "Свяжитесь с нами", faqTitle: "FAQ" },
    about: { title: "О нас", missionTitle: "Наша миссия", offerTitle: "Что мы предлагаем" },
    contact: { title: "Свяжитесь с нами", subtitle: "Мы будем рады вашему сообщению.", submit: "Отправить сообщение", emailUs: "Написать нам" },
    privacy: { title: "Политика конфиденциальности", updated: "Последнее обновление" },
    terms: { title: "Условия использования", updated: "Последнее обновление" },
    blog: { title: "Блог", featured: "Рекомендуем", article: "Статья", readArticle: "Читать статью", previous: "Назад", next: "Далее", page: "Страница", of: "из", backToBlog: "Назад в блог", by: "автор" },
    toolsPage: { title: "Все инструменты", clearSearch: "Очистить поиск", backToTools: "Назад ко всем инструментам", howToUse: "Как использовать", relatedTools: "Похожие инструменты", faqTitle: "Частые вопросы" },
  },
  ar: {
    landing: { heroTitle: "تحكم في مستنداتك الرقمية بسهولة", heroDescription: "مجموعة مجانية من الأدوات عبر الإنترنت لملفات PDF والصور والنصوص.", heroCta: "ابدأ التحويل", toolsTitle: "أدوات شائعة", toolsCta: "عرض كل الأدوات", processTitle: "كيف يعمل", featuresTitle: "لماذا تختارنا", testimonialsTitle: "آراء المستخدمين", landingContactTitle: "تواصل معنا", faqTitle: "الأسئلة الشائعة" },
    about: { title: "من نحن", missionTitle: "مهمتنا", offerTitle: "ما الذي نقدمه" },
    contact: { title: "تواصل معنا", subtitle: "يسعدنا أن نسمع منك.", submit: "إرسال الرسالة", emailUs: "راسلنا" },
    privacy: { title: "سياسة الخصوصية", updated: "آخر تحديث" },
    terms: { title: "شروط الخدمة", updated: "آخر تحديث" },
    blog: { title: "المدونة", featured: "مميز", article: "مقال", readArticle: "اقرأ المقال", previous: "السابق", next: "التالي", page: "صفحة", of: "من", backToBlog: "العودة إلى المدونة", by: "بواسطة" },
    toolsPage: { title: "كل الأدوات", clearSearch: "مسح البحث", backToTools: "العودة إلى كل الأدوات", howToUse: "كيفية الاستخدام", relatedTools: "أدوات ذات صلة", faqTitle: "الأسئلة الشائعة" },
  },
  ko: {
    landing: { heroTitle: "디지털 문서를 더 쉽게 관리하세요", heroDescription: "PDF, 이미지, 텍스트를 위한 무료 온라인 도구 모음입니다.", heroCta: "변환 시작", toolsTitle: "인기 도구", toolsCta: "모든 도구 보기", processTitle: "작동 방식", featuresTitle: "왜 우리를 선택해야 할까요", testimonialsTitle: "사용 후기", landingContactTitle: "문의하기", faqTitle: "FAQ" },
    about: { title: "소개", missionTitle: "우리의 미션", offerTitle: "제공하는 것" },
    contact: { title: "문의하기", subtitle: "여러분의 의견을 듣고 싶습니다.", submit: "메시지 보내기", emailUs: "이메일 보내기" },
    privacy: { title: "개인정보 처리방침", updated: "최종 업데이트" },
    terms: { title: "서비스 약관", updated: "최종 업데이트" },
    blog: { title: "블로그", featured: "추천", article: "게시물", readArticle: "글 읽기", previous: "이전", next: "다음", page: "페이지", of: "/", backToBlog: "블로그로 돌아가기", by: "작성자" },
    toolsPage: { title: "모든 도구", clearSearch: "검색 지우기", backToTools: "모든 도구로 돌아가기", howToUse: "사용 방법", relatedTools: "관련 도구", faqTitle: "자주 묻는 질문" },
  },
  ja: {
    landing: { heroTitle: "デジタル文書をもっと簡単に管理", heroDescription: "PDF、画像、テキスト向けの無料オンラインツール集です。", heroCta: "変換を始める", toolsTitle: "人気のツール", toolsCta: "すべてのツールを見る", processTitle: "使い方", featuresTitle: "選ばれる理由", testimonialsTitle: "ユーザーの声", landingContactTitle: "お問い合わせ", faqTitle: "FAQ" },
    about: { title: "私たちについて", missionTitle: "私たちの使命", offerTitle: "提供内容" },
    contact: { title: "お問い合わせ", subtitle: "ぜひご意見をお聞かせください。", submit: "メッセージを送信", emailUs: "メールする" },
    privacy: { title: "プライバシーポリシー", updated: "最終更新" },
    terms: { title: "利用規約", updated: "最終更新" },
    blog: { title: "ブログ", featured: "注目", article: "記事", readArticle: "記事を読む", previous: "前へ", next: "次へ", page: "ページ", of: "/", backToBlog: "ブログに戻る", by: "著者" },
    toolsPage: { title: "すべてのツール", clearSearch: "検索をクリア", backToTools: "すべてのツールに戻る", howToUse: "使い方", relatedTools: "関連ツール", faqTitle: "よくある質問" },
  },
  ro: {
    landing: { heroTitle: "Gestionează documentele digitale cu ușurință", heroDescription: "Colecția completă de instrumente online gratuite pentru PDF, imagini și text.", heroCta: "Începe conversia", toolsTitle: "Instrumente populare", toolsCta: "Vezi toate instrumentele", processTitle: "Cum funcționează", featuresTitle: "De ce să ne alegi", testimonialsTitle: "Testimoniale", landingContactTitle: "Contactează-ne", faqTitle: "FAQ" },
    about: { title: "Despre noi", missionTitle: "Misiunea noastră", offerTitle: "Ce oferim" },
    contact: { title: "Contactează-ne", subtitle: "Ne-ar plăcea să auzim de la tine.", submit: "Trimite mesajul", emailUs: "Trimite-ne email" },
    privacy: { title: "Politica de confidențialitate", updated: "Ultima actualizare" },
    terms: { title: "Termeni ai serviciului", updated: "Ultima actualizare" },
    blog: { title: "Blog", featured: "Recomandat", article: "Articol", readArticle: "Citește articolul", previous: "Anterior", next: "Următor", page: "Pagina", of: "din", backToBlog: "Înapoi la blog", by: "de" },
    toolsPage: { title: "Toate uneltele", clearSearch: "Șterge căutarea", backToTools: "Înapoi la toate uneltele", howToUse: "Cum se folosește", relatedTools: "Unelte similare", faqTitle: "Întrebări frecvente" },
  },
};

function mergeSiteContent(base: SiteContent, override: DeepPartial<SiteContent>): SiteContent {
  return {
    landing: { ...base.landing, ...override.landing },
    about: { ...base.about, ...override.about },
    contact: {
      ...base.contact,
      ...override.contact,
      labels: { ...base.contact.labels, ...override.contact?.labels },
      placeholders: { ...base.contact.placeholders, ...override.contact?.placeholders },
    },
    privacy: { ...base.privacy, ...override.privacy },
    terms: { ...base.terms, ...override.terms },
    blog: { ...base.blog, ...override.blog },
    toolsPage: { ...base.toolsPage, ...override.toolsPage },
  };
}
